import { api } from '../client';

// ============================================
// Phase 1B: Site Inventory Optimization
// ============================================

export interface SiteDemandForecast {
  itemId: string;
  itemSku: string;
  itemName: string;
  locationId: string;
  locationName: string;
  avgDailyDemand: number;
  demandVariability: number;
  seasonalityDetected: boolean;
  forecastConfidence: number;
  analysisWindowDays: number;
  totalConsumption: number;
  activeWorkOrders: number;
}

export interface SiteStockLevel {
  itemId: string;
  itemSku: string;
  itemName: string;
  locationId: string;
  locationName: string;
  currentStock: number;
  calculatedMinLevel: number;
  calculatedMaxLevel: number;
  safetyStock: number;
  remotenessScore: number;
  avgDailyDemand: number;
  leadTimeDays: number;
  replenishmentCycleDays: number;
  autoReplenishEnabled: boolean;
  stockStatus: 'CRITICAL' | 'LOW' | 'OPTIMAL' | 'EXCESS';
}

export interface ReplenishmentRecommendation {
  itemId: string;
  itemSku: string;
  itemName: string;
  destinationLocation: {
    id: string;
    name: string;
    code: string;
  };
  currentStock: number;
  requiredMinStock: number;
  shortfall: number;
  recommendedQuantity: number;
  urgency: 'CRITICAL' | 'HIGH' | 'MEDIUM' | 'LOW';
  suggestedSourceLocation?: {
    id: string;
    name: string;
    code: string;
    availableStock: number;
    /** Null when either site has no coordinates on file. Never 0 for unknown. */
    distanceMiles: number | null;
  };
  /** Quantity times the item's unit cost, or null when the item has no cost
   *  on file. The cost of moving it is priced in the ERP, not here. */
  estimatedMaterialCost: number | null;
  estimatedLeadTimeDays: number;
}

export interface EmergencyReallocation {
  sourceLocation: {
    id: string;
    name: string;
    code: string;
  };
  availableStock: number;
  transferCost: number;
  leadTimeDays: number;
  downtimeCostSaved: number;
  netBenefit: number;
  riskScore: number;
  recommendation: 'RECOMMENDED' | 'CONDITIONAL' | 'NOT_RECOMMENDED';
  notes: string;
}

export interface SiteOptimizationSummary {
  totalSites: number;
  sitesWithCriticalShortages: number;
  sitesWithExcessStock: number;
  totalReplenishmentNeeds: number;
  totalPotentialSavings: number;
  avgStockEfficiency: number;
}

export const siteOptimizationApi = {
  /**
   * Get demand forecast for specific item at a location
   */
  async getSiteDemandForecast(itemId: string, locationId: string, analysisWindowDays: number = 90) {
    const response = await api.get<SiteDemandForecast>(
      '/inventory/site-optimization/demand-forecast',
      {
        params: { itemId, locationId, analysisWindowDays },
      }
    );
    return response.data;
  },

  /**
   * Calculate optimal stock levels for item at location
   */
  async calculateSiteStockLevels(itemId: string, locationId: string) {
    const response = await api.get<SiteStockLevel>('/inventory/site-optimization/stock-levels', {
      params: { itemId, locationId },
    });
    return response.data;
  },

  /**
   * Get replenishment recommendations across all sites
   */
  async getReplenishmentRecommendations(urgencyFilter?: 'CRITICAL' | 'HIGH' | 'MEDIUM' | 'LOW') {
    const response = await api.get<ReplenishmentRecommendation[]>(
      '/inventory/site-optimization/replenishment',
      {
        params: { urgency: urgencyFilter },
      }
    );
    return response.data;
  },

  /**
   * Evaluate emergency reallocation options
   */
  async evaluateEmergencyReallocation(destinationLocationId: string, itemId: string) {
    const response = await api.get<EmergencyReallocation[]>(
      '/inventory/site-optimization/emergency-reallocation',
      {
        params: { destinationLocationId, itemId },
      }
    );
    return response.data;
  },
};
